import React, { FC } from 'react';
import { SimpleModal } from 'components/Modal';
import { CheckCircleOutline } from '@mui/icons-material';
import { 
  Container, 
  CheckIconContainer, 
  Title,
  Message, 
  LinkSection,
  LinkIntro,
  PublicUrl,
  LinkOutro,
  TempingPromo,
  BoldText,
  RNAPromoCard,
  RNAPromoHeadline,
  RNAPromoBody,
  RNAPromoLink,
  ButtonGroup,
  PostTempingButton,
  CloseButton 
} from './JobPostSuccessModal.styles';
import { StyledButton } from '../../Buttons/DefaultButton/DefaultButton.styles';
import { Type } from 'utils/types';
import Api from 'api';
import { HYGIENIST_TYPES } from 'utils/constants';

interface JobPostSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  role?: Type;
  publicUrl?: string;
  jobType?: number;
  hireJobId?: number;
  onPostTempingShift?: () => void;
  featureError?: string;
  alreadyFeatured?: boolean; // True if the listing was successfully featured during the posting flow
}

export const JobPostSuccessModal: FC<JobPostSuccessModalProps> = ({ 
  isOpen, 
  onClose,
  role,
  publicUrl,
  jobType,
  hireJobId,
  onPostTempingShift,
  featureError,
  alreadyFeatured = false
}) => {


  const roleString = role === Type.Hygienist ? 'Dental Hygienist' : 
                     role === Type.Assistant ? 'Dental Assistant' : 
                     jobType === HYGIENIST_TYPES.FRONT_DESK ? 'Admin/Front Desk' :
                     jobType === HYGIENIST_TYPES.ASSOCIATE_DENTIST ? 'Associate Dentist' : 'position';
  
  const showTempingPromo = jobType !== undefined && jobType <= 1;
  const [featureAvailability, setFeatureAvailability] = React.useState<{ status: 'available' | 'queued' | 'unavailable'; activeCount: number; jobType: number } | null>(null);
  const [featureLoading, setFeatureLoading] = React.useState(false);
  const [featureNowError, setFeatureNowError] = React.useState<string>('');

  // T2-6420: the Recruit North America landing URL is admin-editable. Fetch the
  // configured value when the modal opens; fall back to the hardcoded URL if the
  // config endpoint is unreachable so navigation is never blocked. The visible
  // link text stays unchanged per the ticket — only the destination is dynamic.
  const RNA_FALLBACK_URL = 'https://www.recruitna.com/';
  const [rnaUrl, setRnaUrl] = React.useState<string>(RNA_FALLBACK_URL);

  React.useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    // Reset to the hardcoded fallback at the start of every open so a stale
    // value fetched on a previous open can't briefly show before the refetch
    // resolves (the admin may have changed the URL between opens).
    setRnaUrl(RNA_FALLBACK_URL);
    (async () => {
      try {
        const { url } = await Api.getJobBoardPartnerLink();
        if (!cancelled && url) setRnaUrl(url);
      } catch (error) {
        // Keep the safe fallback already in state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  React.useEffect(() => {
    (async () => {
      if (!hireJobId) return;
      try {
        const av = await Api.getFeatureAvailability(hireJobId);
        setFeatureAvailability(av);
      } catch (error: any) {
        console.error('[Featured Listing] Failed to check feature availability:', {
          hireJobId,
          error: error.message,
          timestamp: new Date().toISOString()
        });
      }
    })();
  }, [hireJobId]);

  const handleFeatureNow = async () => {
    if (!hireJobId) return;
    setFeatureLoading(true);
    setFeatureNowError(''); // Clear any previous errors
    try {
      await Api.featureListing(hireJobId);
      // simple confirmation; dentist can see detailed status on details page
      setFeatureAvailability({ status: 'available', activeCount: 0, jobType: (jobType as any) ?? 0 });
    } catch (error: any) {
      console.error('[Featured Listing] Failed to feature listing from success modal:', {
        hireJobId,
        error: error.message,
        status: error.status,
        response: error.response?.data,
        timestamp: new Date().toISOString()
      });
      
      const errorMsg = error?.response?.data?.error || error?.message || 'Failed to feature listing';
      setFeatureNowError(`Failed to feature listing: ${errorMsg}. Please try again from the job details page.`);
    }
    setFeatureLoading(false);
  };

  const openRecruitNAUrl = () => {
    const cordova = typeof window !== 'undefined' ? (window as any).cordova : null;

    if (cordova?.InAppBrowser?.open) {
      cordova.InAppBrowser.open(rnaUrl, '_system', 'location=yes');
      return;
    }

    window.open(
      rnaUrl,
      cordova ? '_system' : '_blank',
      cordova ? 'location=yes' : 'noopener,noreferrer'
    );
  };

  const handleRecruitNALinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (hireJobId) {
      Api.trackRecruitNAClick(hireJobId, rnaUrl).catch(() => {});
    }

    e.stopPropagation();
    e.preventDefault();
    openRecruitNAUrl();
  };
  
  // Ensure external link opens properly even if Framework7 tries to intercept
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    // The default behavior should work with the 'external' class,
    // but this ensures it opens in a new tab if Framework7 still intercepts
    if (publicUrl) {
      window.open(publicUrl, '_blank', 'noopener,noreferrer');
      e.preventDefault();
    }
  };
  
  return (
    <SimpleModal isOpen={isOpen} onClose={onClose}>
      <Container>
        <CheckIconContainer>
          <CheckCircleOutline style={{ fontSize: 56, color: '#28a745' }} />
        </CheckIconContainer>
        
        <Title>
          Nicely done!
        </Title>
        
        <Message>
          Your job posting for a {roleString} is now live!
          <br />
          Check your email, we sent you a confirmation of the details.
        </Message>
        
        {/* Display featuring error if it occurred during post */}
        {featureError && (
          <div style={{
            margin: '16px 0',
            padding: '12px 16px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            color: '#856404'
          }}>
            <strong>⚠️ Note:</strong> {featureError}
          </div>
        )}
        
        {/* Display featuring error if it occurred from "Feature Now" button */}
        {featureNowError && (
          <div style={{
            margin: '16px 0',
            padding: '12px 16px',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '8px',
            color: '#721c24'
          }}>
            <strong>❌ Error:</strong> {featureNowError}
          </div>
        )}
        
        {publicUrl && (
          <LinkSection>
            <LinkIntro>The link to your job posting is here:</LinkIntro>
            <PublicUrl 
              href={publicUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="external"
              onClick={handleLinkClick}
            >
              {publicUrl}
            </PublicUrl>
            <LinkOutro>You can share this on social media, or other job board sites to attract the best candidates to your job.</LinkOutro>
          </LinkSection>
        )}
        
        {showTempingPromo && (
          <TempingPromo>
            Need a temp {roleString} to fill in while you're waiting for candidates?
            <br />
            <BoldText>Our temping service is best-in-class.</BoldText>
          </TempingPromo>
        )}

        <RNAPromoCard>
          <RNAPromoHeadline>Looking for a more hands-on hiring experience?</RNAPromoHeadline>
          <RNAPromoBody>
            We've partnered with <strong>Recruit North America</strong>, a white-glove hiring
            service for dental offices. If you'd like dedicated support finding the right
            candidate, their team can help.
          </RNAPromoBody>
          <RNAPromoLink
            href={rnaUrl}
            target={typeof window !== 'undefined' && (window as any).cordova ? '_system' : '_blank'}
            rel="noopener noreferrer"
            className="external"
            onClick={handleRecruitNALinkClick}
          >
            Learn more at recruitna.com &rarr;
          </RNAPromoLink>
        </RNAPromoCard>

        <ButtonGroup>
          {/* Feature this listing CTA - only show if not already featured during posting flow */}
          {hireJobId && !alreadyFeatured && (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginRight: 8,
              border: '1px solid var(--primary-blue, #0B4598)',
              borderRadius: 8,
              padding: 12,
              background: 'rgba(11,69,152,0.06)'
            }}>
              <div style={{ fontWeight: 700, color: 'var(--primary-blue, #0B4598)' }}>⭐ Feature this listing</div>
              {featureAvailability?.status === 'unavailable' ? (
                <div style={{ fontSize: 13 }}>Feature unavailable for this listing (Ontario-only for V1).</div>
              ) : (
                <div style={{ fontSize: 13 }}>
                  {featureAvailability?.status === 'queued'
                    ? '📋 Featured Listing slots are filled — Join the waitlist now. No charge until your slot opens and you view your first candidate.'
                    : '✓ Feature slot available — Feature your listing immediately. $25 + HST, charged on first candidate view.'}
                </div>
              )}
              {featureAvailability?.status !== 'unavailable' && (
                <button
                  className="button button-fill color-blue"
                  disabled={featureLoading}
                  onClick={handleFeatureNow}
                  style={{ marginTop: 8 }}
                >
                  {featureAvailability?.status === 'queued' ? 'Join Feature Waitlist' : 'Feature Now ($25 + HST)'}
                </button>
              )}
            </div>
          )}
          {showTempingPromo && onPostTempingShift && (
            <PostTempingButton
              variant="contained"
              size="large"
              onClick={onPostTempingShift}
              disableElevation
            >
              Post a Temping Shift Now
            </PostTempingButton>
          )}
          
          <CloseButton
            variant="contained"
            color="primary"
            size="large"
            onClick={onClose}
          >
            CLOSE
          </CloseButton>
        </ButtonGroup>
      </Container>
    </SimpleModal>
  );
};